import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType, tagsGuide } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import {
  ChevronCompactLeftIcon,
  ChevronCompactRightIcon,
  PlayCircleFilledIcon,
} from "../legacy-ui/icons";
import { useInputMode } from "../../utils/hooks/useInputMode";
import LiveStartCountdown from "../common/LiveStartCountdown";
import {
  getFutureLiveStartTimestamp,
  isLiveContent,
} from "../../utils/liveStartCountdown";
import { focusHomeMenu, resetHomeViewport } from "./focusHelpers";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";

const Pagination = ({ activeIndex, length, onDotClick }) => {
  return (
    <div className="legacy-top-slider-pagination">
      {Array.from({ length }, (_, index) => (
        <div
          onClick={() => onDotClick(index)}
          key={index}
          className={"legacy-top-slider-dot" + (index === activeIndex ? " is-active" : "")}
        />
      ))}
    </div>
  );
};

const MovieItem = ({
  item = {},
  onFocus,
  onBlur,
  fixFrame = false,
  focusKey,
  focusable,
  itemIndex,
  totalSlides,
  onSlideRequest,
  forceFocus,
  nextPlayGuide,
}) => {
  const navigate = useNavigate();
  const { slug, contentPhotos, title, titleEN, kind, contentType } = item || {};
  const liveStartTimestamp = isLiveContent(item) ? getFutureLiveStartTimestamp(item) : null;
  const handleNavigation = () => {
    const contentKind = contentType?.kind || kind;
    const path = `/${contentKind}/${slug}`;
    if (contentKind === "music" && nextPlayGuide) {
      navigate(path, {
        state: NextPlayGuideHelper.withGuideNavigationState(null, nextPlayGuide),
      });
    } else {
      navigate(path);
    }
  };
  const { focused, ref, focusSelf } = useFocusable({
    focusKey,
    focusable,
    onFocus,
    onBlur,
    onEnterPress: handleNavigation,
    onArrowPress: (direction) => {
      if (direction === "left") {
        if (totalSlides > 1) onSlideRequest((itemIndex + 1) % totalSlides);
        return false;
      }

      if (direction === "right") {
        if (itemIndex > 0) {
          onSlideRequest(itemIndex - 1);
          return false;
        }

        focusHomeMenu();
        return false;
      }
      return true;
    },
    forceFocus,
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized slider image with LQIP and WebP support
  const sliderImageData = getOptimizedImageForType("sliderCover", contentPhotos, "TV", { raw: true });
  const videoLogoData = getOptimizedImageForType("videoLogo", contentPhotos, "TV", { raw: true });
  const hasTag = (tagCode) =>
    item.tags?.some((tagLabel) => tagsGuide[tagLabel] === tagCode);
  const heroTag = hasTag("TURKISH_REPORT")
    ? { src: "/tags/gozaresh-large.svg", alt: "گزارش ترکی", width: 150 }
    : hasTag("TURKISH_NATIVE")
      ? { src: "/tags/turkish-alt.svg", alt: "ترکی دیلینده", width: 250 }
      : null;

  // Sidebar width is 360px, so slide width = 100vw - 360px
  const SIDEBAR_WIDTH = 250;
  const slideWidth = `calc(100vw - ${SIDEBAR_WIDTH}px)`;



  if (fixFrame) {
    return (
      <div ref={ref} className="cursor-pointer" onMouseEnter={handleMouseEnter} onClick={handleNavigation}>
        <div className="legacy-top-slider-slide" style={{ width: slideWidth, minWidth: slideWidth }}>
          <div className="legacy-top-slider-image-layer">
            <img
              src={"/fix_frame_iran.png"}
              className="legacy-top-slider-image"
              alt={title}
            />
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div
        onClick={handleNavigation}
        onMouseEnter={handleMouseEnter}
        className="cursor-pointer"
      >
        <div className="legacy-top-slider-slide" style={{ width: slideWidth, minWidth: slideWidth }}>
          <div className="legacy-top-slider-image-layer">
            <img
              src={sliderImageData.src}
              className="legacy-top-slider-image"
              alt={title}
            />
          </div>
          {liveStartTimestamp && (
            <LiveStartCountdown
              startTimestamp={liveStartTimestamp}
              size="hero"
              style={{
                position: "absolute",
                bottom: '10%',
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 60,
              }}
            />
          )}
          <div className="legacy-top-slider-foreground">
            {heroTag && (
              <img
                src={heroTag.src}
                alt={heroTag.alt}
                width={heroTag.width}
              />
            )}
            <div className="legacy-top-slider-meta-row">
              <div className="legacy-top-slider-meta-main">
                <div className="legacy-top-slider-title-row">
                  <span className="legacy-top-slider-title">{title}</span>
                  <div className="legacy-top-slider-title-divider" />
                  <span className="legacy-top-slider-title-en">{titleEN}</span>
                </div>
                <button ref={ref} className={"legacy-top-slider-watch-button" + (focused ? " is-focused" : "")}>
                  <PlayCircleFilledIcon
                    className="ml-2"
                    style={{ color: "#F0F0F0", fontSize: "24px" }}
                  />
                  تماشا کن
                </button>
              </div>
              {videoLogoData.src && (
                <div className="legacy-top-slider-logo-wrap">
                  <OptimizedImage
                    alt={title}
                    src={videoLogoData.src}
                    style={{ width: 365 }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
};

const AUTOPLAY_INTERVAL_MS = 6000;
const PAUSE_DURATION_MS = 5000;

export const TopSlider = ({ movies, onFocus, id, index, nextPlayGuide = null }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const slideFocusKeyPrefix = "top_slider_item_" + id + "_";
  const defaultFocusAppliedRef = useRef(false);

  const { ref, focusKey } = useFocusable({
    onFocus,
    autoRestoreFocus: true,
    focusKey: "slider_" + id,
    preferredChildFocusKey: slideFocusKeyPrefix + activeIndex,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusable: false,
    trackChildren: true,
  });

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  // --- Autoplay refs ---
  const autoplayRef = useRef(null);     // setInterval id
  const pauseTimerRef = useRef(null);   // setTimeout id for resuming after interaction
  const isVisibleRef = useRef(true);    // IntersectionObserver visibility flag
  const isPausedRef = useRef(false);    // Whether autoplay is interaction-paused
  const hasSpatialFocusRef = useRef(false); // Keep focused slides from being disabled by autoplay
  const activeIndexRef = useRef(activeIndex); // Keep a ref in-sync for interval callback

  const totalSlides = movies?.length || 0;

  // Keep the ref in sync with state
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // --- Autoplay helpers ---
  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (hasSpatialFocusRef.current) return;
    if (totalSlides <= 1) return; // nothing to cycle
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % totalSlides);
    }, AUTOPLAY_INTERVAL_MS);
  }, [totalSlides]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }, []);

  const pauseAutoplay = useCallback(() => {
    stopAutoplay();
    isPausedRef.current = true;
    // Clear any previous resume timer
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      isPausedRef.current = false;
      if (isVisibleRef.current && !hasSpatialFocusRef.current) {
        startAutoplay();
      }
    }, PAUSE_DURATION_MS);
  }, [stopAutoplay, startAutoplay]);

  // --- IntersectionObserver: stop autoplay when offscreen ---
  useEffect(() => {
    const node = ref?.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          // Only restart if not interaction-paused
          if (!isPausedRef.current) startAutoplay();
        } else {
          stopAutoplay();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, startAutoplay, stopAutoplay]);

  // --- Initial autoplay start & full cleanup ---
  useEffect(() => {
    if (totalSlides > 1) startAutoplay();
    return () => {
      stopAutoplay();
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, [totalSlides, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (totalSlides < 1 || defaultFocusAppliedRef.current) return undefined;

    var resetHomeScroll = function () {
      if (typeof document === "undefined") return;
      var scrollNode = document.querySelector(".legacy-route-scroll");
      resetHomeViewport(scrollNode);
    };
    var timers = [];

    defaultFocusAppliedRef.current = true;
    resetHomeScroll();
    timers.push(setTimeout(function () {
      setFocus(slideFocusKeyPrefix + activeIndex, { skipScroll: true });
      resetHomeScroll();
    }, 0));

    return function () {
      for (var i = 0; i < timers.length; i += 1) {
        clearTimeout(timers[i]);
      }
    };
  }, [activeIndex, slideFocusKeyPrefix, totalSlides]);

  // --- Slide transform: translate based on activeIndex ---
  const SIDEBAR_WIDTH = 250;
  useLayoutEffect(() => {
    if (!innerRef.current) return;
    // Each slide is (100vw - SIDEBAR_WIDTH). Calculate px offset.
    const slideWidthPx = window.innerWidth - SIDEBAR_WIDTH;
    innerRef.current.style.transform = `translateX(${activeIndex * slideWidthPx}px)`;
  }, [activeIndex]);

  const onAssetFocus = useCallback(
    () => {
      hasSpatialFocusRef.current = true;
      pauseAutoplay();
    },
    [pauseAutoplay]
  );
  const onAssetBlur = useCallback(
    () => {
      hasSpatialFocusRef.current = false;
      pauseAutoplay();
    },
    [pauseAutoplay]
  );

  // --- Navigation handlers (pause autoplay on interaction) ---
  const goNext = useCallback(() => {
    setActiveIndex((prev) => (totalSlides > 1 && prev + 1 < totalSlides ? prev + 1 : 0));
    pauseAutoplay();
  }, [totalSlides, pauseAutoplay]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => {
      if (totalSlides <= 1) return prev;
      return prev - 1 >= 0 ? prev - 1 : totalSlides - 1;
    });
    pauseAutoplay();
  }, [totalSlides, pauseAutoplay]);

  const goToSlide = useCallback((idx) => {
    setActiveIndex(idx);
    pauseAutoplay();
  }, [pauseAutoplay]);

  const requestSlideFocus = useCallback(
    (idx) => {
      if (idx < 0 || idx >= totalSlides) return;
      setActiveIndex(idx);
      pauseAutoplay();
      setTimeout(function () {
        setFocus(slideFocusKeyPrefix + idx, { skipScroll: true });
      }, 0);
    },
    [pauseAutoplay, slideFocusKeyPrefix, totalSlides]
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className="legacy-top-slider-row"
        ref={ref}
        tabIndex={index}
        data-home-top-slider-focus-key={focusKey}
      >
        <div className="legacy-top-slider-scroller" ref={scrollingRef}>
          <div className="legacy-top-slider-content" ref={innerRef}>
            {movies?.map((item, idx) => (
              <MovieItem
                item={item?.content}
                key={"ts_" + item.content?.slug + idx}
                focusKey={slideFocusKeyPrefix + idx}
                focusable={idx === activeIndex}
                itemIndex={idx}
                totalSlides={totalSlides}
                onSlideRequest={requestSlideFocus}
                forceFocus={idx === 0}
                nextPlayGuide={nextPlayGuide}
                onBlur={onAssetBlur}
                onFocus={(e) => {
                  onAssetFocus(e);
                  setActiveIndex(idx);
                }}
              />
            ))}
          </div>
          <div className="legacy-top-slider-nav-zone legacy-top-slider-nav-zone-left">
            <button className="legacy-top-slider-nav legacy-top-slider-nav-left" onClick={goNext}>
              <ChevronCompactLeftIcon
                className="cursor-pointer"
                color="white"
                fontSize={48}
              />
            </button>
          </div>
          <div className="legacy-top-slider-nav-zone legacy-top-slider-nav-zone-right">
            <button className="legacy-top-slider-nav legacy-top-slider-nav-right" onClick={goPrev}>
              <ChevronCompactRightIcon
                className="cursor-pointer"
                color="white"
                fontSize={48}
              />
            </button>
          </div>
          <div className="legacy-top-slider-pagination-zone">
            <Pagination
              onDotClick={goToSlide}
              activeIndex={activeIndex}
              length={totalSlides}
            />
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
